import { useEffect, useState } from "react";
import { fetchMemory, saveMemory } from "../../api";

type Status = "idle" | "loading" | "saving" | "saved" | "error";

export function MemoryEditor() {
  const [content, setContent] = useState("");
  const [pristine, setPristine] = useState("");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    let cancelled = false;
    fetchMemory()
      .then((m) => {
        if (cancelled) return;
        setContent(m.content);
        setPristine(m.content);
        setStatus("idle");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = content !== pristine;

  const save = async () => {
    setStatus("saving");
    try {
      const m = await saveMemory(content);
      setContent(m.content);
      setPristine(m.content);
      setStatus("saved");
      // Drop the success indicator after a moment so the UI doesn't lie.
      setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="memory-editor">
      <div className="memory-textarea-wrap">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={
            status === "loading"
              ? ""
              : "What should the assistant know about you and your work? (Injected into every conversation's system prompt.)"
          }
          disabled={status === "loading"}
        />
        {(status === "loading" || status === "saving") && (
          <span className="memory-textarea-overlay" aria-live="polite">
            {statusLabel(status)}
          </span>
        )}
      </div>
      <div className="memory-actions">
        <span className="memory-status">
          {status === "saved" || status === "error" ? statusLabel(status) : null}
        </span>
        <button
          type="button"
          className="memory-reset"
          onClick={() => {
            setContent(pristine);
            setStatus("idle");
          }}
          disabled={!dirty || status === "saving"}
          title="Discard unsaved changes"
        >
          Reset
        </button>
        <button type="button" onClick={save} disabled={!dirty || status === "saving"}>
          {status === "saving" ? (
            <>
              Saving
              <span className="thinking-dots" aria-hidden="true" />
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </div>
  );
}

function statusLabel(s: Status): React.ReactNode {
  switch (s) {
    case "loading":
      return (
        <>
          loading
          <span className="thinking-dots" aria-hidden="true" />
        </>
      );
    case "saving":
      return (
        <>
          saving
          <span className="thinking-dots" aria-hidden="true" />
        </>
      );
    case "saved":
      return "saved";
    case "error":
      return "load/save failed";
    default:
      return "";
  }
}
